import { ErrorBoundary } from "react-error-boundary";
import { Outlet } from "react-router-dom";
import * as React from "react";
import Box from "@mui/material/Box";
import { 
  Typography, 
  Avatar, 
  Chip, 
  Container,
  Fade,
  Slide 
} from "@mui/material";
import { 
  TrendingUp, 
  Assessment, 
  Business 
} from "@mui/icons-material";

import {AdminNavbar} from "../shared-components/sidebar/SideBar";

export default function AdminStickLayout(props: any): React.ReactElement {

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      {/* MODERN HEADER */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #2C3E50 100%)",
          borderRadius: "25px 25px 25px 25px",
          boxShadow: "0 20px 60px rgba(102, 126, 234, 0.3)",
          color: "white",
          p: 4,
          m: 2,
          mt: 4,
          border: "1px solid rgba(255,255,255,0.1)",
          position: "relative",
          overflow: "hidden",
          backdropFilter: "blur(20px)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, rgba(255,255,255,0.2), transparent 50%, rgba(255,255,255,0.05))",
            pointerEvents: "none",
          }
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 3 }}>
            {/* LEFT SECTION - TITLE AND DESCRIPTION */}
            <Fade in={true} timeout={1200}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, flex: 1 }}>
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      background: "linear-gradient(135deg, #ffffff40, #ffffff10)",
                      border: "3px solid rgba(255,255,255,0.3)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                      animation: "pulse 3s infinite ease-in-out",
                      fontSize: "2rem"
                    }}
                  >
                    💼
                  </Avatar>
                  <Box
                    sx={{
                      position: "absolute",
                      top: -5,
                      right: -5,
                      width: 20,
                      height: 20,
                      background: "linear-gradient(45deg, #4CAF50, #45a049)",
                      borderRadius: "50%",
                      border: "2px solid white",
                      animation: "pulse 2s infinite",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 8,
                        height: 8,
                        background: "white",
                        borderRadius: "50%"
                      }
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Slide direction="up" in={true} timeout={1000}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: "bold", 
                        mb: 1,
                        background: "linear-gradient(135deg, #ffffff, #f0f8ff)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        backgroundSize: "200% 200%",
                        animation: "shimmer 3s ease-in-out infinite",
                        lineHeight: 1.2
                      }}
                    >
                      💼 Lending Business Management System
                    </Typography>
                  </Slide>
                  
                  <Slide direction="up" in={true} timeout={1200}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: "rgba(255,255,255,0.9)", 
                        fontWeight: "400",
                        opacity: 0.95,
                        lineHeight: 1.4
                      }}
                    >
                      Advanced Loan Portfolio Management & Analytics Platform
                    </Typography>
                  </Slide>
                </Box>
              </Box>
            </Fade>

            {/* RIGHT SECTION - STATS AND STATUS */}
            <Fade in={true} timeout={1500}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    icon={<TrendingUp sx={{ color: "white !important" }} />}
                    label="Live System"
                    sx={{
                      background: "linear-gradient(135deg, rgba(76, 175, 80, 0.9), rgba(76, 175, 80, 0.6))",
                      color: "white",
                      fontWeight: "bold",
                      border: "1px solid rgba(255,255,255,0.3)",
                      boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
                      animation: "pulse 3s infinite"
                    }}
                  />
                  <Chip
                    icon={<Assessment sx={{ color: "rgba(255,255,255,0.8) !important" }} />}
                    label="Real-time Analytics"
                    sx={{
                      background: "rgba(255,255,255,0.15)",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.3)",
                      backdropFilter: "blur(10px)",
                      fontWeight: "medium"
                    }}
                  />
                  <Chip
                    icon={<Business sx={{ color: "white !important" }} />}
                    label="Enterprise Ready"
                    sx={{
                      background: "linear-gradient(135deg, rgba(33, 150, 243, 0.9), rgba(33, 150, 243, 0.6))",
                      color: "white",
                      fontWeight: "bold",
                      border: "1px solid rgba(255,255,255,0.3)",
                      boxShadow: "0 4px 15px rgba(33, 150, 243, 0.3)",
                    }}
                  />
                </Box>
              </Box>
            </Fade>
          </Box>

          {/* SUBHEADER */}
          <Fade in={true} timeout={1800}>
            <Box sx={{ mt: 3, p: 2, background: "rgba(255,255,255,0.05)", borderRadius: "15px", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(10px)" }}>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center", lineHeight: 1.6 }}>
                🚀 Streamlined solutions for tracking debits, calculating interest, managing reminders, and monitoring outstanding balances 
                • Seamless customer relationship management • Advanced reporting dashboard
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      <AdminNavbar />
    
      {/* MAIN CONTENT */}
      <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, px: 2 }}>
        <ErrorBoundary fallback={<div>Something went wrong</div>}>
          <Outlet />
        </ErrorBoundary>
      </Box>

      {/* Global Styles */}
      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes shimmer {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
            @keyframes pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
          `
        }}
      />
    </Box>
  );
}
